import type { AnalyticsGrain } from '@pams/shared';
import {
  SERIES_SEGMENT_CLASSES,
  formatCount,
  seriesHeights,
  seriesPointLabel,
  type SeriesPoint,
} from '@/lib/analyticsReport';
import { cn } from '@/lib/utils';
import { USAGE_ANALYTICS_COPY } from '../copy';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  A TIME SERIES AS CSS BARS, WITH ITS SENTENCE AND ITS TABLE. [ANALYTICS §5]
 *
 *  ⚠️ R2: THE BARS ARE `aria-hidden` AND THE SUMMARY IS THE CONTENT. The caller writes the
 *  sentence (it knows what the series means); this renders it, and a `<details>` table carries
 *  every figure the bars draw — a reader who cannot see the plot gets the same numbers, and a
 *  sighted one can check a bar.
 *
 *  ⚠️ THE HEIGHT IS AN INLINE STYLE, AND THAT IS THE ONE THING THAT MAY BE. A data-driven
 *  dimension is `RankedList`'s width precedent; the COLOUR is never inline — it is a literal
 *  class from `SERIES_SEGMENT_CLASSES`, and a second series is told from the first by lightness
 *  alone (R1).
 *
 *  ⚠️ A `<details>`, NOT AN OVERLAY. It is none of the six components `census.spec.ts` sweeps and
 *  it keeps its table in the DOM while closed, so the figures stay addressable under test.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const SeriesBars = ({
  testId,
  series,
  points,
  grain,
  summary,
}: {
  testId: string;
  /**
   * The segment names, bottom segment first — one per `values` entry. ⚠️ AT MOST TWO, BY TYPE:
   * there are two lightness steps, and a third series would have to repeat one of them, which is
   * two series a reader cannot tell apart.
   */
  series: readonly [string] | readonly [string, string];
  points: readonly SeriesPoint[];
  grain: AnalyticsGrain;
  summary: string;
}) => {
  const heights = seriesHeights(points);
  const first = points[0];
  const last = points[points.length - 1];
  return (
    <div className="flex min-w-0 flex-col gap-2" data-testid={testId}>
      <div aria-hidden data-testid={`${testId}-plot`} className="flex flex-col gap-1">
        <div className="flex h-24 items-end gap-px border-b border-border">
          {points.map((point, index) => (
            <span key={point.at} data-point={point.at} className="flex h-full min-w-0 flex-1 flex-col-reverse">
              {series.map((name, segment) => (
                <span
                  key={name}
                  data-segment={segment}
                  className={cn('block w-full', SERIES_SEGMENT_CLASSES[segment === 0 ? 0 : 1])}
                  style={{ height: `${String(heights[index]?.[segment] ?? 0)}%` }}
                />
              ))}
            </span>
          ))}
        </div>
        {first && last ? (
          <div className="flex justify-between gap-3 text-xs text-muted-foreground">
            <span>{seriesPointLabel(first.at, grain)}</span>
            <span>{seriesPointLabel(last.at, grain)}</span>
          </div>
        ) : null}
      </div>
      {series.length > 1 ? (
        <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground" data-testid={`${testId}-legend`}>
          {series.map((name, segment) => (
            <li key={name} className="flex items-center gap-1.5">
              <span aria-hidden className={cn('size-2.5 rounded-sm', SERIES_SEGMENT_CLASSES[segment === 0 ? 0 : 1])} />
              {name}
            </li>
          ))}
        </ul>
      ) : null}
      <p className="text-sm text-foreground">{summary}</p>
      <details className="text-sm">
        <summary className="w-fit cursor-pointer text-muted-foreground hover:text-foreground">
          {USAGE_ANALYTICS_COPY.series.showFigures}
        </summary>
        <table className="mt-2 w-full text-left text-xs tabular-nums">
          <thead>
            <tr className="text-muted-foreground">
              <th scope="col" className="py-1 pr-3 font-medium">
                {USAGE_ANALYTICS_COPY.series.time(grain)}
              </th>
              {series.map((name) => (
                <th key={name} scope="col" className="py-1 pr-3 text-right font-medium">
                  {name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {points.map((point) => (
              <tr key={point.at}>
                <th scope="row" className="py-1 pr-3 font-normal text-muted-foreground">
                  {seriesPointLabel(point.at, grain)}
                </th>
                {series.map((name, segment) => (
                  <td key={name} className="py-1 pr-3 text-right text-foreground">
                    {formatCount(point.values[segment] ?? 0)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </div>
  );
};
